/**
 * Address helper utilities
 */

import type { AddressFormState } from '../types/address'
import type { SharedResidenceDateFields } from '../types/utils'
import { ADDRESS_CONSTANTS, type ResidenceTypeApi } from '@/constants/address'
import type { Address, AddressData } from '@/types/application'

/**
 * Convert MM/DD/YYYY to YYYY-MM-DD format
 */
export const formatDateForAPI = (dateStr: string): string => {
  if (!dateStr) return ''
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr
  const parts = dateStr.split('/')
  if (parts.length === 3) {
    const [month, day, year] = parts
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
  }
  return dateStr
}

/**
 * Convert YYYY-MM-DD to MM/DD/YYYY format
 */
export const formatDateForDisplay = (dateStr: string): string => {
  if (!dateStr) return ''
  if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) return dateStr
  const parts = dateStr.split('-')
  if (parts.length === 3) {
    const [year, month, day] = parts
    return `${month}/${day}/${year}`
  }
  return dateStr
}

/**
 * Calculate months between two dates (30-day rule, floor).
 * Used for address duration, etc.
 */
export const calculateMonthsBetween = (startDateStr: string, endDateStr?: string): number => {
  if (!startDateStr) return 0
  const startDate = new Date(formatDateForAPI(startDateStr))
  const endDate = endDateStr ? new Date(formatDateForAPI(endDateStr)) : new Date()
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return 0

  // Calculate difference in days
  const diffTime = endDate.getTime() - startDate.getTime()
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

  // Every 30 days is 1 month (floor)
  const months = Math.floor(diffDays / 30)

  return Math.max(0, months)
}

/**
 * Calendar months between two dates (matches Angular moment().diff(..., 'months')).
 * Returns the number of full calendar months from start to end.
 * Used for employment job_duration and job_gap to match Angular behavior.
 */
const parseLocalDate = (apiDateStr: string): Date => {
  const [yr, mo, dy] = apiDateStr.split('-').map(Number)
  return new Date(yr, mo - 1, dy)
}

export const calendarMonthsBetween = (startDateStr: string, endDateStr?: string): number => {
  if (!startDateStr) return 0
  const apiStart = formatDateForAPI(startDateStr)
  const startDate = parseLocalDate(apiStart)
  const endDate = endDateStr ? parseLocalDate(formatDateForAPI(endDateStr)) : new Date()
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return 0
  if (endDate.getTime() < startDate.getTime()) return 0

  let months = (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth())
  if (endDate.getDate() < startDate.getDate()) months -= 1
  return Math.max(0, months)
}

/**
 * Months at job for employment (matches Angular: job_duration = monthsDiff + 1).
 * Angular: if start >= today (day precision), returns 0. Otherwise calendar months + 1 (inclusive).
 */
export const employmentMonthsAtJob = (startDateStr: string, endDateStr?: string): number => {
  if (!startDateStr) return 0
  const startDate = parseLocalDate(formatDateForAPI(startDateStr))
  startDate.setHours(0, 0, 0, 0)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  // Angular: moment(start).isBefore(moment(today), 'day') — if NOT strictly before today, return 0
  if (startDate >= today) return 0
  const months = calendarMonthsBetween(startDateStr, endDateStr)
  return months + 1
}

export const MAX_CURRENT_EMPLOYMENT_START_DAYS_FUTURE = 90

export const getMaxCurrentEmploymentStartDate = (): Date => {
  const maxDate = new Date()
  maxDate.setHours(0, 0, 0, 0)
  maxDate.setDate(maxDate.getDate() + MAX_CURRENT_EMPLOYMENT_START_DAYS_FUTURE)
  return maxDate
}

export const isFutureEmploymentStartDate = (startDateStr: string): boolean => {
  if (!startDateStr) return false
  const startDate = new Date(formatDateForAPI(startDateStr))
  if (isNaN(startDate.getTime())) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  startDate.setHours(0, 0, 0, 0)
  return startDate > today
}

export const isEmploymentStartDateWithinAllowedFuture = (startDateStr: string): boolean => {
  if (!startDateStr) return true
  const startDate = new Date(formatDateForAPI(startDateStr))
  if (isNaN(startDate.getTime())) return false
  const maxDate = getMaxCurrentEmploymentStartDate()
  startDate.setHours(0, 0, 0, 0)
  return startDate <= maxDate
}

/**
 * Angular parity: previous-employment / other-source job start dates use `notfutureDateValidator`,
 * which is invalid when the date is NOT strictly before today (day precision) — i.e. today OR later.
 * (Angular: `!moment(value).isBefore(moment(new Date()), 'day')` → { dateInvalid: true }.)
 *
 * Returns true when the date violates the rule (today or in the future).
 * Parses as a LOCAL date so `new Date('YYYY-MM-DD')` UTC-midnight parsing can't let "today" slip past
 * (the previous `> end-of-today` check allowed today and was off by a day in non-UTC timezones).
 */
export const violatesNotFutureDate = (dateStr: string): boolean => {
  if (!dateStr) return false
  const startDate = parseLocalDate(formatDateForAPI(dateStr))
  if (isNaN(startDate.getTime())) return false
  startDate.setHours(0, 0, 0, 0)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return startDate >= today
}

/**
 * Get move-in date based on residence type
 */
export const getMoveInDate = (state: AddressFormState): string => {
  if (state.residenceType === 'rent') return state.moveInDateRent
  if (state.residenceType === 'own') return state.moveInDateOwn
  if (state.residenceType === 'family') return state.moveInDateFamily
  return ''
}

function firstNonEmptyTrimmed(...vals: (string | undefined)[]): string {
  for (const v of vals) {
    const t = (v ?? '').trim()
    if (t) return t
  }
  return ''
}

/**
 * Copy move-in and planned end (lease / planned move-out) across rent/own/family slots
 * so changing residence type does not drop dates the user already entered.
 */
export function syncSharedResidenceDateFields(
  slice: SharedResidenceDateFields,
): SharedResidenceDateFields {
  const mi = firstNonEmptyTrimmed(
    slice.moveInDateRent,
    slice.moveInDateOwn,
    slice.moveInDateFamily,
  )
  const end = firstNonEmptyTrimmed(
    slice.leaseEndDate,
    slice.plannedMoveOutDate,
    slice.plannedMoveOutDateFamily,
  )
  return {
    moveInDateRent: mi || slice.moveInDateRent,
    moveInDateOwn: mi || slice.moveInDateOwn,
    moveInDateFamily: mi || slice.moveInDateFamily,
    leaseEndDate: end || slice.leaseEndDate,
    plannedMoveOutDate: end || slice.plannedMoveOutDate,
    plannedMoveOutDateFamily: end || slice.plannedMoveOutDateFamily,
  }
}

/**
 * Check if address duration is less than the minimum required months
 */
export const shouldShowPreviousAddress = (state: AddressFormState): boolean => {
  if (!state.residenceType) return false
  const moveInDate = getMoveInDate(state)
  if (!moveInDate) return false
  // Use calendarMonthsBetween to match the auto-calculated duration fields and Angular behavior
  return calendarMonthsBetween(moveInDate) < ADDRESS_CONSTANTS.MIN_MONTHS_FOR_PREVIOUS_ADDRESS
}

/**
 * Angular parity: previous-address UI and 24-month history enforcement run only when
 * current_address is valid AND duration at current address is under the threshold
 * (same as `isCurrentAddressValid() && shouldShowPreviousAddress()` on About).
 */
export function shouldEnforceAddressHistoryRules(
  isCurrentAddressValid: boolean,
  durationRequiresPreviousAddress: boolean
): boolean {
  return isCurrentAddressValid && durationRequiresPreviousAddress
}

// Trailing apt/unit pattern: comma + space + # + optional space + alphanumeric (e.g. ", #34", ", # 35"). Require # to avoid stripping "Suite 100" or zip.
const TRAILING_APT_REGEX = /,\s*#\s*[a-z0-9-]+$/i

/**
 * Remove apartment/unit suffix from street_address when loading from API.
 * Strips the current apartment first, then any remaining trailing apt-like suffixes
 * (e.g. "..., #35, #34" with apartment "#34" becomes "..." so we don't re-append and accumulate).
 * Use the result for the main "address" field; keep apartment in the dedicated Apt/Unit field.
 */
export function stripAptFromStreetAddress(
  streetAddress: string,
  apartment: string | null | undefined
): string {
  if (!streetAddress) return streetAddress
  let trimmed = streetAddress.trim()
  // First strip the known apartment suffix so we match how we append (e.g. ", #34")
  if (apartment) {
    const apt = String(apartment).trim()
    if (apt) {
      const withHash = apt.startsWith('#') ? apt : `#${apt}`
      const suffixes = [`, ${withHash}`, `, ${apt}`]
      for (const suffix of suffixes) {
        if (trimmed.endsWith(suffix)) {
          trimmed = trimmed.slice(0, -suffix.length).trim()
          break
        }
      }
    }
  }
  // Remove any remaining trailing apt/unit segments (fixes already-accumulated data)
  while (TRAILING_APT_REGEX.test(trimmed)) {
    trimmed = trimmed.replace(TRAILING_APT_REGEX, '').trim()
  }
  return trimmed
}

/** Format unit for display: ensure leading # if not present */
function formatUnitForDisplay(unit: string | null | undefined): string {
  if (!unit || !String(unit).trim()) return ''
  const trimmed = String(unit).trim()
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`
}

/**
 * Format address for display: "Street Unit, City, State, Zip, Country"
 * Unit number comes under the address section (right after street).
 * Example: "ABC #111, Lux, US" for street=ABC, unit=#111, city=Lux, country=US
 */
export function formatAddressDisplay(addr: {
  house_no?: string | null
  street?: string | null
  street_type?: string | null
  apartment?: string | null
  city?: string | null
  state?: string | null
  zip_code?: string | null
  country?: string | null
}): string {
  const streetPart = [addr.house_no, addr.street].filter(Boolean).join(' ').trim()
  const streetType = addr.street_type?.trim() || ''
  const streetLine = [streetPart, streetType].filter(Boolean).join(' ').trim()
  const unitFormatted = formatUnitForDisplay(addr.apartment ?? '')
  const addressSection = [streetLine, unitFormatted].filter(Boolean).join(' ').trim()
  const locationParts = [addr.city, addr.state, addr.zip_code, addr.country].filter(Boolean)
  const locationSection = locationParts.join(', ').trim()
  return [addressSection, locationSection].filter(Boolean).join(', ')
}

/**
 * Whether Google Maps link should be shown — Angular parity: require non-empty formatted address.
 */
export function hasAddressForMapQuery(
  addr: Parameters<typeof formatAddressDisplay>[0] | null | undefined,
): boolean {
  if (!addr) return false
  return formatAddressDisplay(addr).trim().length > 0
}

/**
 * Build address data object for API
 */
export const buildAddressData = (
  state: AddressFormState,
  existingRaId: number | null = null,
  isPreviousAddress: boolean = false
): Address | null => {
  if (!state.residenceType) return null

  // Prepend "#" to unit/apt when appending to address (avoid double # if user already entered it)
  const formatUnitForAddress = (unit: string): string => {
    const trimmed = unit.trim()
    return trimmed ? (trimmed.startsWith('#') ? trimmed : `#${trimmed}`) : ''
  }

  // Build street address: "Street Unit" only (no city/state/zip here). Prefer parts to avoid duplicate unit.
  let fullStreetAddress: string
  if (state.streetNumber?.trim() && state.streetName?.trim()) {
    const streetPart = `${state.streetNumber.trim()} ${state.streetName.trim()}`
    const aptFormatted = state.aptUnit?.trim() ? formatUnitForAddress(state.aptUnit) : ''
    fullStreetAddress = aptFormatted ? `${streetPart} ${aptFormatted}` : streetPart
  } else {
    fullStreetAddress = state.address || ''
    // If using state.address, strip any existing unit then append once (avoid duplicate on save)
    if (state.aptUnit?.trim() && fullStreetAddress) {
      const baseAddress = stripAptFromStreetAddress(fullStreetAddress.trim(), null)
      const unitFormatted = formatUnitForAddress(state.aptUnit)
      fullStreetAddress = `${baseAddress} ${unitFormatted}`
    }
  }

  // Map residence type
  const addressTypeMap: Record<string, ResidenceTypeApi> = {
    'rent': ADDRESS_CONSTANTS.RESIDENCE_TYPES.RENT,
    'own': ADDRESS_CONSTANTS.RESIDENCE_TYPES.OWN,
    'family': ADDRESS_CONSTANTS.RESIDENCE_TYPES.FAMILY,
  }
  const addressType = addressTypeMap[state.residenceType] || ADDRESS_CONSTANTS.RESIDENCE_TYPES.RENT

  // Build address_data
  // Get currently_living value based on residence type (ensure it's always defined)
  const getCurrentlyLivingValue = (): boolean => {
    if (state.residenceType === 'rent') {
      return state.stillHereRent !== undefined ? state.stillHereRent : true
    } else if (state.residenceType === 'own') {
      return state.stillHereOwn !== undefined ? state.stillHereOwn : true
    } else if (state.residenceType === 'family') {
      return state.stillHereFamily !== undefined ? state.stillHereFamily : true
    }
    return true // Default to true if residence type is not set
  }

  const addrData: Partial<AddressData> = {
    // API expects "1" for true and "0" for false (string format)
    currently_living: getCurrentlyLivingValue() ? '1' : '0'
  }

  if (state.residenceType === 'rent') {
    if (state.moveInDateRent) addrData.moveIn_date = formatDateForAPI(state.moveInDateRent)
    if (!state.stillHereRent && state.leaseEndDate) addrData.moveOut_date = formatDateForAPI(state.leaseEndDate)
    if (state.landlordName) addrData.landlord_name = state.landlordName
    if (state.landlordMobile) addrData.landlord_mobile_no = state.landlordMobile.replace(/\D/g, '')
    if (state.landlordEmail) addrData.landlord_email = state.landlordEmail
    if (state.rentAmount) addrData.rent_amount = state.rentAmount.replace(/[^0-9.]/g, '')
    addrData.landlord_relationship_type = null
    // Angular parity: RENT uses paid_rent (months) for both current and previous address
    if (state.monthsPaidRent) {
      addrData.paid_rent = parseInt(state.monthsPaidRent, 10) || 0
    } else {
      addrData.paid_rent = null
    }
  } else if (state.residenceType === 'own') {
    if (state.moveInDateOwn) addrData.moveIn_date = formatDateForAPI(state.moveInDateOwn)
    if (!state.stillHereOwn && state.plannedMoveOutDate) addrData.moveOut_date = formatDateForAPI(state.plannedMoveOutDate)
    if (isPreviousAddress) {
      // Previous address OWN: use monthsResided (auto-calculated from dates, like Angular month_reside)
      if (state.monthsResided) addrData.month_reside = parseInt(state.monthsResided, 10) || 0
    } else {
      // Current address OWN: user enters mortgage payment, month_reside is auto-calculated
      if (state.monthlyMortgagePayment) addrData.rent_amount = state.monthlyMortgagePayment.replace(/[^0-9.]/g, '')
      if (state.moveInDateOwn) {
        let endDate: string | undefined = undefined
        if (!state.stillHereOwn) {
          endDate = state.plannedMoveOutDate
        }
        const months = calendarMonthsBetween(state.moveInDateOwn, endDate)
        if (months >= 0) addrData.month_reside = months
      }
    }
  } else if (state.residenceType === 'family') {
    if (state.moveInDateFamily) addrData.moveIn_date = formatDateForAPI(state.moveInDateFamily)
    if (!state.stillHereFamily && state.plannedMoveOutDateFamily) addrData.moveOut_date = formatDateForAPI(state.plannedMoveOutDateFamily)
    if (state.contactName) addrData.landlord_name = state.contactName
    if (state.contactMobile) addrData.landlord_mobile_no = state.contactMobile.replace(/\D/g, '')
    if (state.contactEmail) addrData.landlord_email = state.contactEmail
    if (state.contactWebsite) addrData.landlord_website = state.contactWebsite
    if (state.contactRelationship) addrData.landlord_relationship_type = state.contactRelationship
    if (state.rentAmountFamily) addrData.rent_amount = state.rentAmountFamily.replace(/[^0-9.]/g, '')
    // FAMILY uses paid_rent (months) for previous address; same as RENT
    if (state.monthsPaidRent) {
      addrData.paid_rent = parseInt(state.monthsPaidRent, 10) || 0
    } else {
      addrData.paid_rent = null
    }
  }

  const stillHere = getCurrentlyLivingValue()
  // Single move-out field on API: clear when still living; only the active type's branch may set it above
  if (stillHere) {
    addrData.moveOut_date = null
  }

  // Strip fields that belong to other residence types so merged API payloads do not keep stale RENT/OWN/FAMILY data
  const rt = state.residenceType
  if (rt === 'rent') {
    addrData.month_reside = null
    if (addrData.landlord_website === undefined) addrData.landlord_website = null
  } else if (rt === 'own') {
    addrData.paid_rent = null
    addrData.landlord_name = null
    addrData.landlord_mobile_no = null
    addrData.landlord_email = null
    addrData.landlord_website = null
    addrData.landlord_relationship_type = null
  } else if (rt === 'family') {
    addrData.month_reside = null
  }

  // Determine if address is outside US based on country code
  // outside_us should be true when country is not "US"
  const countryCode = (state.country || ADDRESS_CONSTANTS.DEFAULT_COUNTRY).toUpperCase()
  const isOutsideUS = countryCode !== 'US' || state.outsideUS

  return {
    ra_id: existingRaId,
    street_address: fullStreetAddress,
    outside_us: isOutsideUS,
    house_no: state.streetNumber || undefined,
    street: state.streetName || undefined,
    street_type: state.streetType || '',
    apartment: state.aptUnit || null,
    city: state.city || undefined,
    state: state.state || undefined,
    zip_code: state.zipCode || undefined,
    country: state.country || ADDRESS_CONSTANTS.DEFAULT_COUNTRY,
    address_type: addressType,
    address_data: addrData
  }
}

